import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { useSiteInfo, COOKIE_CONSENT_KEY } from "@/lib/siteInfo";

// Carica gli strumenti di misurazione SOLO dopo il consenso e solo se configurati nel .env
function loadAnalytics(info) {
  if (window.__dlcAnalyticsLoaded) return;
  window.__dlcAnalyticsLoaded = true;
  if (info.ga_measurement_id) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${info.ga_measurement_id}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", info.ga_measurement_id, { anonymize_ip: true });
  }
  if (info.posthog_key) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `${info.posthog_host || "https://eu.i.posthog.com"}/static/array.js`;
    s.onload = () => {
      if (window.posthog && window.posthog.init) {
        window.posthog.init(info.posthog_key, { api_host: info.posthog_host || "https://eu.i.posthog.com", persistence: "localStorage" });
      }
    };
    document.head.appendChild(s);
  }
}

export default function CookieBanner() {
  const { t } = useLang();
  const info = useSiteInfo();
  const [choice, setChoice] = useState(() => localStorage.getItem(COOKIE_CONSENT_KEY));

  useEffect(() => {
    const reopen = () => setChoice(null);
    window.addEventListener("cookie:preferences", reopen);
    return () => window.removeEventListener("cookie:preferences", reopen);
  }, []);

  useEffect(() => {
    if (info && info.other_cookies && choice === "accepted") loadAnalytics(info);
  }, [info, choice]);

  const decide = useCallback((value) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, value);
    setChoice(value);
  }, []);

  if (!info || !info.other_cookies || choice) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-[90] p-4 sm:p-6"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.45 }}
        data-testid="cookie-banner"
      >
        <div className="mx-auto max-w-4xl card-dark p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-5 shadow-2xl">
          <Cookie className="h-6 w-6 text-copper shrink-0" />
          <p className="text-sm text-cream2 leading-relaxed flex-1">
            {t("cookie.text")}{" "}
            <Link to="/cookie-policy" className="link-underline text-copperlight" data-testid="cookie-banner-policy-link">
              {t("legal.cookie")}
            </Link>
          </p>
          <div className="flex gap-3 shrink-0">
            <button onClick={() => decide("rejected")} className="btn-ghost !px-4 !py-2 !text-xs" data-testid="cookie-reject-btn">
              {t("cookie.reject")}
            </button>
            <button onClick={() => decide("accepted")} className="btn-copper !px-4 !py-2 !text-xs" data-testid="cookie-accept-btn">
              {t("cookie.accept")}
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
